<template>
  <PageWrapper v-if="dataLoaded">
    <BlogIntro
      :article="article"
      :permalink="permalink"
    />
    <section
      id="article"
      class="px-1 py-5"
    >
      <div class="container">
        <div class="row justify-center">
          <div class="lg:col-9 xl:col-8 text">
            <!-- Text-->
            <div
              class="text"
              v-html="article.text"
            />
          </div>
        </div>
      </div>
    </section>
    <BlogOtherArticles />
  </PageWrapper>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'

import RetailApiV2 from '@/api/retailApi/RetailApiV2'
import BlogIntro from '@/components/blog/BlogIntro.vue'
import BlogOtherArticles from '@/components/blog/BlogOtherArticles.vue'
import PageWrapper from '@/components/general/BasePageWrapper.vue'
import useHead from '@/mixins/headManager'
import { useNavbarStore } from '@/stores/NavbarStore'

const { t } = useI18n()
const route = useRoute()
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const url = (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.path
const { params } = route
const permalink = ref()

const dataLoaded = ref(false)
const article = ref({})

const { setNavbar } = useNavbarStore()

onBeforeRouteUpdate((to, from, next) => {
  permalink.value = to.params.permalink
  initPage()
  next()
})

useHead({
  meta: [
    {
      property: `og:description`,
      content: t('og_description'),
    },
    {
      name: `description`,
      content: t('og_description'),
    },
    {
      property: `og:url`,
      content: url,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: url,
    },
  ],
})

const initPage = async () => {
  const { data } = await RetailApiV2.get(`articles/${permalink.value}`)
  article.value = data.article
  dataLoaded.value = true
  setNavbar({
    backgroundColor: 'bg-primary-750',
  })
}

onMounted(() => {
  permalink.value = params.permalink
  initPage()
})
</script>

<style lang="scss">
#article {
  img {
    &.fr-fil {
      left: 5px;
      margin: 5px auto 5px 0;
    }
    &.fr-fir {
      margin: 5px 0 5px auto;
      right: 5px;
    }
  }

  img.fr-dib {
    display: block;
    float: none;
    margin: 5px auto;
    vertical-align: top;
    max-width: 100%;
  }

  img.fr-dii {
    display: inline-block;
    float: none;
    margin: 5px auto;
    vertical-align: top;
    max-width: 100%;
  }
}
</style>

<i18n>
  {
    "cs": {
    "blog": "Blog",
    "og_description": "Hledáte inspiraci na zdravé recepty nebo vás zajímá co s tubusy od Mixit? Čtěte na blogu Mixit.cz."
},
    "sk": {
    "blog": "Blog",
    "og_description": "Hľadáte inšpiráciu na zdravé recepty alebo vás zaujíma čo s tubusmi od Mixit? Čítajte na blogu Mixit.sk."
},
    "pl": {
    "blog": "Blog",
    "og_description": "Szukasz inspiracji na zdrowe przepisy lub chcesz zobaczyć co można zrobić z tub Mixit? O wszystkim przeczytasz na blogu Mixit.pl."
},
    "hu": {
    "blog": "Blog",
    "og_description": "Egészséges receptekhez keresel ihletet, vagy érdekel, mihez kezdj a fennmaradt Mixit tubusokkal? Erről és még sok minden másról olvashatsz a Mixit.hu blogon."
},
    "de": {
    "blog": "Blog",
    "og_description": "Suchst du nach Inspirationen für gesunde Rezepte oder fragst du dich, was man mit den Dosen von Mixit anfangen soll? Lies im Mixit-Blog."
},
    "at": {
    "blog": "Blog",
    "og_description": "Suchst du nach Inspirationen für gesunde Rezepte oder fragst du dich, was man mit den Dosen von Mixit anfangen soll? Lies im Mixit-Blog."
},
    "ro": {
    "blog": "Blog",
    "og_description": "Cauți inspirație pentru rețete sănătoase sau te întrebi ce poți face cu aceste cutii Mixit? Citește despre asta și multe altele pe blogul Mixit.ro."
}
  }
</i18n>
